<template>
	<section>
		<!-- item information -->
		<div id="detail">
			<!-- media header -->
			<div
				class="media-container side-padding"
				:class="{ 'has-images': images.length }"
			>
				<router-link
					v-if="search_query"
					id="back-btn"
					class="label"
					:to="{ path: `${search_url}?${url_query}` }"
				>
					<span class="icon-back-arrow" aria-hidden="true"></span>
					<span class="hide-mobile">{{ $t("labels.back_to_results") }}</span>
					<span class="hide-flex-desktop">Back</span>
				</router-link>
				<div class="print-export-btns">
					<button id="print" @click="printPage">
						<span class="label-small hide-mobile">{{ $t("labels.print_detail") }}</span>
					</button>
				</div>

				<ImageViewer
					:images="images"
					:alt="item.title"
					v-if="images.length > 0"
				/>
			</div>

			<article class="fields side-padding">
				<div class="head">
					<h1>{{ item.title }}</h1>
					<div class="save-input">
						<button type="button" @click="updateSave({id: item.id, checkLimit: true}, $event)">
							<span
								class="icon-checkbox hidden"
								:class="{ saved: isSaved(item.id) }"
								aria-hidden="true"
							></span>
							<span
								class="icon-bookmark"
								:class="{ saved: isSaved(item.id) }"
								aria-hidden="true"
							></span>
							<span class="text" v-if="isSaved(item.id)">{{
								$t("labels.remove_from_list")
							}}</span>
							<span class="text" v-else>{{
								$t("labels.save_to_list")
							}}</span>
							<span class="text sr-only" aria-hidden="true">{{ $t("labels.save_to_list") }}</span>
						</button>
					</div>
				</div>
				<div class="actions">
					<div class="results-nav-short" v-if="next.id || prev.id">
						<router-link
							v-if="prev.id"
							class="label-small prev"
							:to="{ name: `${prev.type}-detail`, params: { id: prev.id } }"
						>
							{{ $t("labels.previous_result") }}
						</router-link>
						<p v-else class="label-small disabled prev" aria-hidden="true">
							{{ $t("labels.previous_result") }}
						</p>
						<p class="label-small disabled" aria-hidden="true">
							{{ paginationCount }}
						</p>

						<router-link
							v-if="next.id"
							class="label-small next"
							:to="{ name: `${next.type}-detail`, params: { id: next.id } }"
						>
							{{ $t("labels.next_result") }}
						</router-link>
						<p v-else class="label-small disabled next" aria-hidden="true">
							{{ $t("labels.next_result") }}
						</p>
					</div>
				</div>

				<!-- item fields -->
				<dl class="info">
					<div v-for="f in fields" :key="f.name" :id="f.name">
						<aside
							id="expand_style"
							v-if="
								f.name == 'biography_or_history' ||
									f.name == 'scope_and_content'
							"
						>
							<details>
								<summary>
									<dt>{{ f.displayName }}</dt>
									<span class="expand-icon" aria-hidden="true"></span>
								</summary>
								<dd v-for="v in f.value" :key="v.id">
									{{ v }}
								</dd>
							</details>
						</aside>
						<div v-else>
							<dt>{{ f.displayName }}</dt>

							<dd v-if="f.isWebLink">
								<div class="web-resource" v-for="(r, i) in f.value" :key="i">
									<span>{{ r.note }}</span>
									<a :href="r.source" target="_blank">{{ r.source }}</a>
								</div>
							</dd>

							<dd v-else-if="f.isOrigins">
								<span v-for="(l, i) in f.value" :key="i">
									<router-link
										class="field-link-inline color-dark"
										:to="l.path"
										>{{ l.text }}</router-link
									>
									<span v-if="i < f.value.length - 1"> – </span>
								</span>
							</dd>
							<dd v-else-if="f.hasLinks" v-for="l in f.value" :key="l.text">
								<router-link class="field-link color-dark" :to="l.path">{{
									l.text
								}}</router-link>
							</dd>
							<dd v-else-if="f.isItemLink" v-for="l in f.value" :key="l.text">
								<router-link class="field-link color-dark" :to="l.path">{{
									l.text
								}}</router-link>
							</dd>
							<dd v-else-if="typeof f.value == 'string'">
								{{ f.value }}
							</dd>
							<dd v-else-if="typeof f.value != 'object'">
								{{ f.value.media }}

								<div class="html-field" v-html="f.value"></div>
							</dd>
							<dd v-else v-for="v in f.value" :key="v.id">
								<div v-if="f.media" class="media-field">
									<div v-if="!isVideo(v) && !isAudio(v)">
										<p>{{ v.title }}</p>
										<p v-if="v.note">{{ v.note }}</p>
										<a
											:href="v.url"
											download
											target="_blank"
											class="button download-file"
											:class="getSite !== 'cwm' ? 'cmh' : 'cwm'"
											:title="mediaTitle(v)"
											>{{ $t("labels.view_file") }}: {{ v.filename }}</a
										>
									</div>
									<figure v-if="isAudio(v)">
										<figcaption>
											<p v-if="v.title">{{ v.title }}</p>
											<p v-if="v.note">{{ v.note }}</p>
											<a
												:href="v.url"
												download
												class="button download-file"
												:title="mediaTitle(v)"
												>{{ v.filename }}</a
											>
										</figcaption>
										<audio controls :src="v.url">
											Your browser does not support the
											<code>audio</code> element.
										</audio>
									</figure>
									<figure v-if="isVideo(v)">
										<figcaption>
											<p v-if="v.title">{{ v.title }}</p>
											<p v-if="v.note">{{ v.note }}</p>
											<a
												:href="v.url"
												download
												class="button download-file"
												:title="mediaTitle(v)"
												>{{ v.filename }}</a
											>
										</figcaption>

										<video controls>
											<source :src="v.url" :type="videoType(v)" />
											Sorry, your browser doesn't support embedded videos.
										</video>
									</figure>
								</div>

								<span class="list-field expand_style" v-else v-html="v"></span>
							</dd>
						</div>
					</div>
				</dl>

				<div>
					<!-- fonds section -->
					<section class="holdings-hier-sections" v-if="context">
						<details>
							<summary class="record-context">
								<h2 class="label">{{ $t("detail_page.record_context") }}</h2>
								<span class="expand-icon" aria-hidden="true"></span>
							</summary>

							<div class="context-content">
								<dl>
									<div
										v-for="(items, title) in context"
										:key="title"
										class="level"
									>
										<dt class="h5">{{ title }}</dt>
										<div>
											<dd v-for="link in items.links" :key="link.id">
												<p v-if="link.id == item.id">{{ link.title }}</p>
												<router-link
													v-else
													:to="{
														name: 'archive-detail',
														params: { id: link.id },
													}"
												>
													{{ link.title }}
												</router-link>
											</dd>
										</div>
									</div>
								</dl>
							</div>
						</details>
					</section>

					<!-- holdings section -->
					<section class="holdings-hier-sections" v-if="holdings.length">
						<h2 class="label">{{ $t("detail_page.holdings") }}</h2>
						<div class="table-container">
							<table class="holdings-table">
								<thead>
									<tr>
										<th></th>
										<th class="label">
											{{ $t("detail_page.holdings_header_call_no") }}
										</th>
										<th class="label">
											{{ $t("detail_page.holdings_header_availablity") }}
										</th>
									</tr>
								</thead>
								<tr v-for="(h, i) in holdings" :key="h.id">
									<td>{{ String(i + 1).padStart(2, "0") }}</td>
									<td>{{ h.call_num }}</td>
									<td>{{ h.availability }}</td>
								</tr>
							</table>
						</div>
					</section>
				</div>

				<!-- collapsable information -->
				<aside class="collapse-info">
					<details>
						<summary class="share label">
							<span>{{ $t("detail_page.share_button_text") }}</span>
							<span class="expand-icon" aria-hidden="true"></span>
						</summary>

						<div class="share-content">
							<div class="permalink">
								<p>{{ $t("detail_page.share_link_label") }}</p>
								<input
									id="copy_link"
									type="text"
									:value="permLink"
									style="width:60%;padding:4px 6px;margin-left:6px;"
								/>
								<button
									class="copy_button"
									@mouseleave="outFunc"
									@click="copyLink"
								>
									<span id="myTooltip" class="tooltiptext">{{
										$t("labels.copy_to_clipboard")
									}}</span>
									<span>{{ $t("labels.copy_link") }}</span>
								</button>
							</div>
							<div>
								<p>{{ $t("detail_page.share_by") }}</p>
								<ul>
									<li>
										<a :href="`mailto:?body=${permLink}&subject=${item.title}`">
											{{ $t("detail_page.share_email_label") }}
										</a>
									</li>
									<li>
										<a
											:href="
												`https://www.facebook.com/sharer/sharer.php?u=${permLink}`
											"
										>
											{{ $t("detail_page.share_fb_label") }}
										</a>
									</li>
									<li>
										<a
											:href="
												`https://twitter.com/intent/tweet?text=${permLink}`
											"
										>
											{{ $t("detail_page.share_twi_label") }}
										</a>
									</li>
								</ul>
							</div>
						</div>
					</details>
					<details>
						<summary class="cataloging label">
							<span>{{ $t("detail_page.cataloguing_button_text") }}</span>
							<span class="expand-icon" aria-hidden="true"></span>
						</summary>

						<div class="cataloging-content">
							<p v-html="$t(`detail_page.cataloguing_text.${site}`)"></p>
						</div>
					</details>
					<details>
						<summary class="rights label">
							<span>{{ $t("detail_page.rights_button_text") }}</span>
							<span class="expand-icon" aria-hidden="true"></span>
						</summary>

						<div class="rights-content">
							<p v-html="$t(`detail_page.rights_text.${site}`)"></p>
						</div>
					</details>
				</aside>
			</article>

			<div class="results-nav" v-if="next.id || prev.id">
				<router-link
					v-if="prev.id"
					class="label prev"
					:to="{ name: `${prev.type}-detail`, params: { id: prev.id } }"
				>
					{{ $t("labels.previous_result") }}
				</router-link>
				<p v-else class="label disabled prev" aria-hidden="true">
					{{ $t("labels.previous_result") }}
				</p>

				<p class="label disabled" aria-hidden="true">
					{{ paginationCount }}
				</p>

				<router-link
					v-if="next.id"
					class="label next"
					:to="{ name: `${next.type}-detail`, params: { id: next.id } }"
				>
					{{ $t("labels.next_result") }}
				</router-link>
				<p v-else class="label disabled next" aria-hidden="true">
					{{ $t("labels.next_result") }}
				</p>
			</div>
		</div>

		<!-- related items and additional links -->
		<section class="end-page-content">
			<!-- Disable related items for now.  should this be replaced by related objects instead?
            <section v-if="related_items.length" class="side-padding">
                <h2>{{ $t('labels.related_results') }}</h2>
                <ul v-if="item_type == 'artifact'" class="related-items">
                     <li v-for="i in related_items" v-bind:key="i.id">
                        <router-link :to="{name: 'artifact-detail', params: {id: i.id }}" class="block-link">
                            <figure class="image-container">
                                <img v-if="i.media" :src="i.media.url" :alt="i.title">
                            </figure>
                            <span class="h3">
                                <span>{{ i.title }}</span>
                            </span>
                        </router-link>
                    </li>
                </ul>
                <ul v-else class="related-items">
                     <li v-for="i in related_items" v-bind:key="i.id">
                        <router-link :to="{name: 'archive-detail', params: {id: i.id }}" class="block-link">
                            <figure class="image-container">
                                <img v-if="i.media" :src="i.media.url" :alt="i.title">
                            </figure>
                            <span class="h3">
                                <span>{{ i.title }}</span>
                            </span>
                        </router-link>
                    </li>
                </ul>
            </section>
        -->
			<SearchingForMore></SearchingForMore>
			<ResourceCenter></ResourceCenter>
		</section>
		<JsonLD :data="json_ld"></JsonLD>
	</section>
</template>

<script>
import { mapGetters } from "vuex";
import JsonLD from "../components/JsonLD";
import ResourceCenter from "../components/ResourceCenter";
import SearchingForMore from "../components/SearchingForMore";
import ImageViewer from "@/components/ImageViewer";
import SavedListMixin from "../mixins/SavedListMixin";
import store from "../store/index";
import i18n from "../i18n/index";

export default {
	mixins: [SavedListMixin],
	components: {
		JsonLD,
		ResourceCenter,
		SearchingForMore,
		ImageViewer,
	},
	head() {
		return {
			title: function() {
				return {
					inner: this.item.title,
					separator: "|",
					complement: this.$i18n.t(`main.${this.site}.museum_name`),
				};
			},
			meta: function() {
				return [
					{
						rel: "canonical",
						href:
							this.$i18n.t(`main.${this.site}.cannonical`) + this.$route.path,
						id: "rel-con",
					},
					{
						rel: "alternate",
						href:
							this.$i18n.t(`main.${this.site}.alternate`) + this.$route.path,
						hreflang: this.$i18n.t(`main.${this.site}.alternate_locale`),
						id: "rel-alt",
					},

					{ name: "description", content: this.desc, id: "desc" },

					{
						property: "og:title",
						content: `${this.item.title} | ${this.$i18n.t(
							`main.${this.site}.museum_name`
						)}`,
						id: "og-title",
					},
					{ property: "og:description", content: this.desc, id: "og-desc" },
					{ property: "og:type", content: "website", id: "og-type" },
					{
						property: "og:locale",
						content: this.$i18n.locale,
						id: "og-locale",
					},
					{
						property: "og:image",
						content: `${window.location.host}${this.$i18n.t(
							`main.${this.site}.preview_image`
						)}`,
						id: "og-image",
					},
				];
			},
		};
	},
	data: function() {
		return {
			relatedItems: [],
		};
	},
	computed: {
		...mapGetters({
			item: "detail/item",
			item_type: "detail/type",
			item_loading: "detail/item_loading",
			item_loaded: "detail/item_loaded",
			search_query: "search/query",
			url_query: "search/url_query",
			search_type: "search/search_type",
			related_items: "detail/related_items",
			traversal_list: "results/traversal_list",
			json_ld: "detail/json_ld",
			site: "search/site",
		}),
		desc: function() {
			if (this.type == "archive") {
				return this.item.scope_and_content || this.item.title;
			} else {
				return this.item.description || this.item.title;
			}
		},
		type_view: function() {
			return this.type == "archive" ? "grid" : "list";
		},
		paginationCount: function() {
			let TotalCount = this.traversal_list.map((item) => item.id);
			let apple;
			TotalCount.forEach((val, i) => {
				if (val === this.item.id) apple = i;
			});
			return apple + 1 + "/" + TotalCount.length;
			//let result = TotalCount.filter((x) => x === "3328057");
		},
		prev: function() {
			if (!this.item) return;
			let currentItemInd = this.traversal_list.findIndex(
				(i) => i.id == this.item.id
			);

			if (currentItemInd > 0) {
				return this.traversal_list[currentItemInd - 1];
			} else {
				return {};
			}
		},
		next: function() {
			if (!this.item) return;
			let currentItemInd = this.traversal_list.findIndex(
				(i) => i.id == this.item.id
			);

			if (currentItemInd < this.traversal_list.length - 1) {
				return this.traversal_list[currentItemInd + 1];
			} else {
				return {};
			}
		},
		media: function() {
			if (!this.item) return [];
			return this.item.media
				? this.item.media.filter((m) => m.mime_type != "image")
				: [];
		},
		images: function() {
			if (!this.item) return [];
			return this.item.media
				? this.item.media.filter((m) => m.mime_type == "image")
				: [];
		},
		permLink: function() {
			return window.location.href;
		},
		holdings: function() {
			if (!this.item) return;
			return this.item.holdings ? this.item.holdings : [];
		},
		context: function() {
			if (!this.item) return;
			let context = [];

			if (!this.item.relations) return;

			if (this.item.relations.level) {
				let levelObj = {};
				let level = this.item.relations.level.toLowerCase();
				levelObj["title"] = level;
				levelObj["links"] = [
					{
						title: this.item.relations["name"],
						id: this.item.id,
					},
				];

				context.push(levelObj);
			}

			if (this.item.relations) {
				let relations = this.item.relations;
				for (let key in relations) {
					let levelObj = {
						links: [],
					};
					levelObj["title"] = key.toLowerCase();
					if (key == "parent") {
						let item = relations[key];
						levelObj["links"].push({
							title: item.title,
							id: item.id,
						});
					} else {
						relations[key].forEach((item) => {
							levelObj["links"].push({
								title: item.title,
								id: item.id,
							});
						});
					}
					context.push(levelObj);
				}
			}

			let fonds = context[context.findIndex((item) => item.title == "fonds")];
			let files = context[context.findIndex((item) => item.title == "files")];
			let series = context[context.findIndex((item) => item.title == "series")];
			let parent = context[context.findIndex((item) => item.title == "parent")];
			let items = context[context.findIndex((item) => item.title == "items")];
			let collections =
				context[context.findIndex((item) => item.title == "collections")];
			let sub_fonds =
				context[context.findIndex((item) => item.title == "sub-fonds")];
			let sub_series =
				context[context.findIndex((item) => item.title == "sub-series")];
			let sub_sub_series =
				context[context.findIndex((item) => item.title == "sub-sub-series")];
			let unknowns =
				context[context.findIndex((item) => item.title == "undefined")];

			let contextObj = {};
			// the following is in order we would like to display.
			// Beside the parent, all other categories should be at the same leaf level.
			// If an element has a children object, those should be in the next leaf level ( need to expand to display)
			// To get the label for category name (eg: sub-fonds) for the current language look at item.level
			if (parent) {
				contextObj["parent"] = parent;
			}
			if (fonds) {
				contextObj["fonds"] = fonds;
			}
			if (collections) {
				contextObj["collections"] = collections;
			}
			if (sub_fonds) {
				contextObj["sub-fonds"] = sub_fonds;
			}
			if (series) {
				contextObj["series"] = series;
			}
			if (sub_series) {
				contextObj["sub-series"] = sub_series;
			}
			if (sub_sub_series) {
				contextObj["sub-sub-series"] = sub_sub_series;
			}
			if (files) {
				contextObj["files"] = files;
			}
			if (items) {
				contextObj["items"] = items;
			}
			if (unknowns) {
				contextObj["undefined"] = unknowns;
			}

			return contextObj;
		},
		fields: function() {
			let type = this.$route.name.split("-")[0];
			let fieldMap = this.$i18n.messages[this.$i18n.locale].detail_page
				.field_map[type];
			let linkFields = this.$i18n.messages[this.$i18n.locale].detail_page
				.field_map.has_links;
			let fields = [];

			for (const field in this.item) {
				if (field == "arrangements") {
					if (this.item[field].level) {
						let link = this.makeLinkField({
							fieldName: `${field}.level`,
							value: this.item[field].level,
							type: type,
						});
						fields.push({
							name: `${field}.level`,
							displayName: fieldMap[`${field}.level`],
							value: link,
							hasLinks: true,
						});
					}
					if (this.item[field].name) {
						fields.push({
							name: `${field}.name`,
							displayName: fieldMap[`${field}.name`],
							value: this.item[field].name,
						});
					}
				} else if (field == "places") {
					if (!this.item[field].origins) return;
					let origins = this.item[field].origins[0];
					if (!origins) return;

					let links = [];
					for (let region in origins) {
						let apiKey = `places.origins.${region}`;
						let value = origins[region];

						let link = this.makeLinkField({
							fieldName: apiKey,
							value: value,
							type: type,
						});

						links.push(link);
					}

					fields.push({
						name: field,
						displayName: fieldMap[field],
						value: links.flat(),
						hasLinks: true,
						isOrigins: true,
					});
				} else if (field == "military") {
					let allMilitaryFields = this.item[field];
					for (let key in allMilitaryFields) {
						let apiKey = `military.${key}`;
						let displayName = fieldMap[apiKey];
						let value = allMilitaryFields[key];
						let fieldInfo = {
							name: key,
							displayName: displayName,
							hasLinks: false,
						};
						if (linkFields.includes(apiKey)) {
							fieldInfo.hasLinks = true;
							fieldInfo.value = this.makeLinkField({
								fieldName: apiKey,
								value: value,
								type: type,
							});
						} else {
							if (typeof value != "string") {
								value = value.join(", ");
							}
							fieldInfo.value = value;
						}
						fields.push(fieldInfo);
					}
				} else if (
					Object.keys(fieldMap).includes(field) &&
					field == "related_objects"
				) {
					let fieldInfo = {
						name: field,
						displayName: fieldMap[field],
						isItemLink: true,
					};

					let value = this.item[field];
					let sources = [];
					let related_type = this.item_type;
					value.forEach((relatedObj) => {
						let related_text = relatedObj.title;
						if (Object.keys(relatedObj).includes("relationship")) {
							if (relatedObj.relationship.toLowerCase() == "associated artifact") {
								related_type = "artifact";
							}
							related_text = relatedObj.relationship + ": " + related_text;
						}
						sources.push({
							text: related_text,
							path: `/${related_type}/${relatedObj.id}`,
						});
					});

					fieldInfo.value = sources;
					fields.push(fieldInfo);
				} else if (
					Object.keys(fieldMap).includes(field) &&
					field == "web_resources"
				) {
					let fieldInfo = {
						name: field,
						displayName: fieldMap[field],
						isWebLink: true,
					};

					let value = this.item[field];
					let sources = [];
					value.forEach((resource) => {
						sources.push({
							note: resource["note"],
							source: resource["source"],
						});
					});

					fieldInfo.value = sources;
					fields.push(fieldInfo);
				} else if (Object.keys(fieldMap).includes(field) && field == "media") {
					if (this.media.length) {
						fields.push({
							name: field,
							displayName: fieldMap[field],
							value: this.media,
							media: true,
						});
					}
				} else if (Object.keys(fieldMap).includes(field) && field != "media") {
					let value = this.item[field];
					let fieldInfo = {
						name: field,
						displayName: fieldMap[field],
						hasLinks: false,
					};
					if (linkFields.includes(field)) {
						fieldInfo.hasLinks = true;
						fieldInfo.value = this.makeLinkField({
							fieldName: field,
							value: value,
							type: type,
						});
					} else {
						fieldInfo.value = value;
					}

					fields.push(fieldInfo);
				}
			}

			// sort fields to be in mapping order
			let orderedFields = [];
			for (let key in fieldMap) {
				fields.findIndex((field) => {
					if (field.name == key) {
						orderedFields.push(field);
					}
					if (key.includes("military") || key.includes("arrangements")) {
						let splitKey = key.split(".");
						if (splitKey[1] == field.name) {
							orderedFields.push(field);
						}
					}
				});
			}

			return orderedFields;
		},
		urlQuery() {
			return this.url_query;
		},
		search_url() {
			return this.search_type == "basic"
				? "/"
				: `/${this.$i18n.messages[this.$i18n.locale].routes.advanced_search}/`;
		},
		getSite() {
			return this.site
		}
	},
	methods: {
		mediaTitle(media) {
			return `${media.title}, ${media.mime_type}, ${media.filename}`;
		},
		printPage() {
			window.print();
		},
		copyLink() {
			let copyText = document.getElementById("copy_link");
			copyText.select();
			copyText.setSelectionRange(0, 99999);
			navigator.clipboard.writeText(copyText.value);
			let tooltip = document.getElementById("myTooltip");
			tooltip.innerHTML = this.$t("labels.copied");
		},
		outFunc() {
			let tooltip = document.getElementById("myTooltip");
			tooltip.innerHTML = this.$t("labels.copy_to_clipboard");
		},
		goBack(e) {
			e.preventDefault();
			this.$router.go(-1);
		},
		isVideo(media) {
			return media.mime_type == "video";
		},
		videoType(media) {
			let videoType = media.url.split(".").pop();
			return `video/${videoType}`;
		},
		isAudio(media) {
			return media.mime_type == "audio";
		},
		fetchRelated() {
			if (!this.item) return;
			if (this.item.discipline || this.item.subjects || this.item.category) {
				let terms = ["media.mime_type", "_eq", "image"];
				if (this.item_type == "artifact") {
					if (this.item.category) {
						terms.push("category", this.item.category[0]);
					} else if (this.item.sub_category) {
						terms.push("sub_category", this.item.sub_category[0]);
					}
				} else {
					if (this.item.subject) {
						terms.push("discipline", this.item.subject[0]);
					} else if (this.item.discipline) {
						terms.push("discipline", this.item.discipline[0]);
					}
				}

				this.$store.dispatch("detail/related_items", {
					locale: this.$i18n.locale,
					type: this.item_type,
					q: terms,
					sort: "id",
					order: "asc",
					page: 1,
					size: 4,
				});
			}
		},
		fetchItem(id) {
			return this.$store.dispatch("detail/fetch", {
				locale: this.$i18n.locale,
				id: id,
			});
		},
		updateType(type) {
			this.$store.dispatch("detail/type", type);
		},
		makeLinkField(params) {
			let inputFilters = ["subjects"];
			let inputFilterMap = {
				subjects: "search.subjects",
			};

			let links = [];
			if (!params.value) return;
			let isInputField = inputFilters.includes(params.fieldName);

			let field = isInputField
				? inputFilterMap[params.fieldName]
				: params.fieldName;
			let advRoute = this.$i18n.messages[this.$i18n.locale].routes
				.advanced_search;
			let path = isInputField
				? `/${advRoute}?type=${params.type}&q1=${field}::_contains::`
				: `/${advRoute}?type=${params.type}&${field}=`;

			if (typeof params.value !== "string") {
				params.value.forEach((v) => {
					links.push({
						text: v,
						path: `${path}${v.toLowerCase()}&view=${this.type_view}&page=1`,
					});
				});
			} else {
				links.push({
					text: params.value,
					path: `${path}${params.value.toLowerCase()}&view=${
						this.type_view
					}&page=1`,
				});
			}

			return links;
		},
	},
	mounted() {
		this.fetchRelated();
		this.$emit('updateHead');
	},
	beforeRouteUpdate(to, from, next) {
		this.$store.dispatch("detail/loaded", false);
		this.$store.dispatch("detail/hero", {});

		let type = `${to.name.split("-")[0]}`;
		this.updateType(type);
		this.fetchItem(to.params.id).then(() => {
			this.fetchRelated();
		});
		next();
	},
	beforeRouteEnter(to, from, next) {
		let type = `${to.name.split("-")[0]}`;
		store.dispatch("detail/type", type);
		store.dispatch("detail/loaded", false);
		store
			.dispatch("detail/fetch", {
				locale: i18n.locale,
				id: to.params.id,
			})
			.then(() => next());
	},
};
</script>

<style scoped lang="scss">
#detail {
	display: grid;
	grid-template-columns: 1fr minmax(50vw, 900px) 1fr;
	grid-template-rows: auto auto auto;
	gap: 0px 0px;
	background: $white;

	h1 {
		max-width: 38ch;
		text-transform: unset;
		font-size: 2.25rem;
		color: $dark-gray1;
		text-transform: uppercase;
	}

	a {
		color: $dark-gray1;
	}

	.head{
		padding: 2.875rem 0 0.375rem;
		display: flex;
		justify-content: space-between;

		h1{
			flex-basis: 80%;
			margin: 0 0;
		}
	}
}

.save-input {
	button {
		border: none;
		background: none;
		display: flex;
		align-items: center;
		cursor: pointer;
	}

	.text{
		font-size: 1rem;
		color: $dark-gray1;
		text-decoration: underline;
	}

	.icon-checkbox {
		border: solid 2px $menu-gray;
		display: inline-block;
		height: 18px;
		width: 18px;
		margin-right: 0.5rem;

		&.saved {
			border: $cwm-green 2px solid;
			background: $cwm-green;

			&:after {
				height: 18px;
				content: url("../assets/images/icon-check.svg");
				display: flex;
				justify-content: center;
				align-items: center;
			}
		}
	}

	.icon-bookmark{
		display: inline-block;
		height: 32px;
		width: 20px;
		margin-right: 0.5rem;
		background: url("../assets/images/icon-bookmark-off.svg") center no-repeat;
		background-size: contain;

		&.saved{
			background: url("../assets/images/icon-bookmark-on.svg") center no-repeat;
		}
	}
}

.media-container {
	grid-area: 1 / 1 / 2 / 4;
	position: relative;
	padding: $s2 0;

	&.has-images {
		background: $off-white;
	}

	#back-btn {
		position: absolute;
		left: $side-padding;
		top: $side-padding;
		display: flex;

		.icon-back-arrow {
			margin-right: 0.6rem;

			&:after {
				height: 16px;
				width: 16px;
				content: "";
				background-size: contain;
				background-image: url("../assets/images/icon-arrow-back-dark.svg");
				display: flex;
				justify-content: center;
				align-items: center;
				filter: contrast(1);
			}
		}
	}
	.print-export-btns {
		position: absolute;
		right: $side-padding;
		top: $side-padding;

		button {
			cursor: pointer;
			background: none;
			border: none;
			display: inline-flex;
			align-items: center;
			margin-left: $s2;

			span {
				padding-right: $s1;
				color: $menu-gray;
			}
		}

		#export:after {
			content: url("../assets/images/icon-download-excel.svg");
			display: flex;
			justify-content: center;
			align-items: center;
		}
		#print:after {
			content: url("../assets/images/icon-print.svg");
			display: flex;
			justify-content: center;
			align-items: center;
		}
	}
}

#prev-img {
	grid-column-start: 1;
	grid-row: 1;
}

#next-img {
	grid-column-start: 3;
	grid-row: 1;
}

.img-control-btn {
	height: 20px;
	width: 20px;
	align-self: center;
	justify-self: center;
}

.fields {
	grid-area: 2 / 2 / 3 / 3;

	.actions {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		padding: $s2 0;

		.save-input {
			padding-bottom: $s2;
		}

		#saved {
			margin-right: $s1;
		}

		.results-nav-short {
			border-bottom: $thin-gray-border;
			border-top: $thin-gray-border;

			a,
			p {
				padding: $s1 1.6rem;
				box-sizing: border-box;
				width: 32.5%;
				display: inline-block;
				text-decoration: none;
				color: $dark-gray1;
				line-height: 1rem;
				margin: 0;
				display: inline-flex;
				justify-content: center;
				align-items: center;

				&:first-child {
					border-right: $thin-gray-border;
				}

				&:last-child {
					border-left: $thin-gray-border;
					text-align: right;
				}

				&.disabled {
					opacity: 0.5;
				}

				&.next {
					&:after {
						content: url("../assets/images/icon-arrow-go.svg");
						transform: scale(0.5);
						display: block;
					}
				}

				&.prev {
					flex-direction: row-reverse;
					&:after {
						content: url("../assets/images/icon-arrow-back.svg");
						transform: scale(0.5);
						display: block;
					}
				}
			}
		}
	}

	$col-2-width: 70%;
	dl.info {
		div {
			margin-top: $s2 * 1.1;

			&.html-field {
				margin-top: 0;
			}
		}
		dt {
			width: 30%;
			min-width: 150px;
			font-weight: bold;
			vertical-align: top;
			display: inline-table;
			line-height: $s2;
		}
		dd {
			width: $col-2-width;
			margin-left: 0;
			display: inline-block;
			line-height: $s2;
			font-size: 1rem;

			+ dd {
				margin-left: 30%;
				margin-top: 0.2rem;
			}

			div.media-field {
				margin-top: 0;

				div {
					margin: 0;
				}
				.cwm {
					background-color: $cwm-green-menu;
				}

				.cmh {
					//background-color: $cmh-red;
				}

				a.download-file {
					margin: 0.5rem 0;
					line-height: 16px;
					color: $white !important;
				}
			}

			.web-resource {
				margin: 0;
				span {
					display: block;
				}
				a {
					display: block;
					margin-bottom: 1rem;
				}
			}

			.field-link {
				display: block;
			}
			.field-link-inline {
				display: inline-block;
			}

			.list-field {
				margin-top: 0;
			}

			/deep/ p {
				font-size: 1rem;
				margin: 0;
			}

			/deep/ a {
				color: $dark-gray1;
			}

			/*
            .field-link:not(:last-child) {
                margin-right: .25rem;
                &.color-dark:after {
                    content: ", ";
                    display: inline-block;
                }
            }
            */

			/deep/ .color-dark {
				color: $dark-gray1;
			}

			figure {
				margin: 0.5rem 0;

				figcaption {
					margin-bottom: 0.5rem;
				}
			}

			audio {
				width: 100%;
			}

			video {
				width: 100%;
			}
		}
	}

	.holdings-hier-sections {
		margin-top: $s4;
		h2 {
			display: inline-block;
			margin-bottom: 0.75rem;
		}

		.h4 {
			font-weight: bold;
			display: inline-block;
		}

		.record-context h2 {
			margin-right: $s2;
		}
	}

	.holdings-table,
	.context-content {
		background: rgba($off-white, 0.6);
		border-top: 1px solid $light-gray1;
	}

	.holdings-table {
		width: 100%;
		th {
			border-bottom: 1px solid $light-gray1;
			&.label:not(:last-of-type) {
				border-right: 1px solid $light-gray1;
			}
		}
		th,
		td {
			padding: $s1 $s2;
		}
		tr {
			border-top: 1px solid $light-gray1;
		}
	}

	.context-content {
		padding: $s2;

		dl {
			overflow: hidden;
		}

		p,
		a {
			font-size: 0.895rem;
			line-height: 1.5rem;
			margin: 0;
		}

		.level {
			display: flex;
			align-items: stretch;
			margin-bottom: $s2;
			&:not(:last-of-type) {
				dt:after {
					content: "";
					display: block;
					border-left: 2px solid $light-gray1;
					border-bottom: 2px solid $light-gray1;
					height: 100%;
					width: 40%;
					margin-left: 25%;
					margin-top: 25%;
				}
			}

			dt {
				width: 70px;
				flex-shrink: 0;
				font-size: 0.895rem;
				margin-top: 4px;
			}
			dd {
				margin: 0 0 0.5rem;
			}

			&:nth-child(2) {
				transform: translateX(40px);
				width: 80%;
			}
			&:nth-child(3) {
				transform: translateX(80px);
				width: 60%;
			}
			&:nth-child(4) {
				transform: translateX(120px);
				width: 50%;
			}
		}
	}

	aside {
		width: $col-2-width;
		margin-top: $s3;
		margin-bottom: $s5;
		margin-left: auto;
		border-bottom: $thin-gray-border;

		summary {
			border-top: $thin-gray-border;
			list-style: none;
			padding: $s2 0;
			display: flex;
			align-items: center;
			justify-content: space-between;
			&::marker {
				display: none;
			}

			.expand-icon:after {
				content: url("../assets/images/icon-plus.svg");
				display: flex;
				justify-content: center;
				align-items: center;
			}
		}
		details {
			p {
				margin-top: 0;
				font-size: 1rem;
			}

			/deep/ a {
				color: $dark-gray1;
			}
		}
	}
}

details {
	.expand-icon:after {
		content: url("../assets/images/icon-plus.svg");
		display: inline-flex;
		justify-content: center;
		align-items: center;
	}

	summary {
		cursor: pointer;
	}

	&[open] {
		summary {
			.expand-icon:after {
				content: url("../assets/images/icon-minus.svg");
				display: inline-flex;
				justify-content: center;
				align-items: center;
			}
		}
	}
}

#biography_or_history,
#scope_and_content {
	aside {
		margin-top: 0 !important;
		width: 100% !important;
		margin-top: 0;
		margin-bottom: 0;
	}

	dd:last-of-type {
		margin-bottom: 1.7875rem !important;
	}
	dd:nth-child(2) {
		margin-left: 30% !important;
	}
}

.share-content {
	padding-bottom: $s2;
	a {
		margin-left: $s1;
		line-height: 1.5rem;
		display: block;
	}
	.permalink {
		p,
		a {
			display: inline-block;
		}
	}
}

.results-nav {
	grid-area: 3 / 1 / 4 / 4;
	display: flex;

	border-top: $thin-gray-border;

	a,
	p {
		width: 50%;
		text-align: center;
		padding: $s2;
		line-height: 1.2rem;
		margin: 0;
		display: flex;
		align-items: center;
		justify-content: center;

		&:first-of-type {
			border-right: $thin-gray-border;
		}
		&.disabled {
			opacity: 0.5;
		}

		&.next {
			margin-left: $s2;
			&:after {
				content: url("../assets/images/icon-arrow-go.svg");
				transform: scale(0.5);
				display: block;
			}
		}

		&.prev {
			flex-direction: row-reverse;
			padding-left: 1.5rem;
			margin-right: $s2;
			&:after {
				content: url("../assets/images/icon-arrow-back.svg");
				transform: scale(0.5);
				display: block;
			}
		}
	}
}

.related-items {
	display: flex;

	li {
		flex-basis: 25%;
		padding: $s1;
		box-sizing: border-box;

		&:first-of-type {
			padding-left: 0;
		}
		&:last-of-type {
			padding-right: 0;
		}

		a {
			background: $off-white;
			color: $black;
			text-decoration: none;
			span.h3 span {
				font-size: 1rem;
			}
		}

		.image-container {
			margin: 0;
			background: $light-gray1;
			height: 230px;
			display: flex;

			img {
				object-fit: contain;
				display: block;
				margin: 0 auto;
			}
		}
	}
}

.copy_button {
	margin-left: 6px;
	position: relative;
	display: inline-block;
}

.copy_button .tooltiptext {
	visibility: hidden;
	width: 140px;
	background-color: #555;
	color: #fff;
	text-align: center;
	border-radius: 6px;
	padding: 5px;
	position: absolute;
	z-index: 1;
	bottom: 150%;
	left: 50%;
	margin-left: -75px;
	opacity: 0;
	transition: opacity 0.3s;
}

.copy_button .tooltiptext::after {
	content: "";
	position: absolute;
	top: 100%;
	left: 50%;
	margin-left: -5px;
	border-width: 5px;
	border-style: solid;
	border-color: #555 transparent transparent transparent;
}

.copy_button:hover .tooltiptext {
	visibility: visible;
	opacity: 1;
}

@media print {
	.media-container.has-images {
		background: $white;

		.media-slides {
			ul {
				flex-direction: row;
			}
			.hero-media {
				width: 30%;
			}
		}
	}

	.fields {
		break-before: page;
	}

	.collapse-info,
	.end-page-content {
		display: none;
	}
}

@media screen and (max-width: $mq-max-width-desktop) {
	.media-container {
		padding-top: $s3;

		#back-btn {
			left: $side-padding-mobile;
			top: 1.563rem;
		}
		.print-export-btns {
			right: $side-padding-mobile;
			top: $side-padding-mobile;
		}
	}
}

@media screen and (max-width: $mq-max-width-tablet) {
	.related-items {
		flex-wrap: wrap;
		li {
			flex-basis: 50%;
			box-sizing: border-box;
			padding: 0.5rem;
			&:last-of-type,
			&:first-of-type {
				padding: 0.5rem;
			}
		}
	}

	.fields {
		dl.info {
			dd {
				+ dd {
					margin-left: 0;
					margin-top: 0.2rem;
				}
			}
		}
		.table-container {
			max-width: 100vw;
			overflow-x: scroll;
		}
		.context-content {
			.level {
				&:not(:last-of-type) {
					dt:after {
						content: "";
						display: block;
						border-left: 2px solid $light-gray1;
						border-bottom: 2px solid $light-gray1;
						height: 105%;
						width: 15%;
						margin-left: 25%;
						margin-top: 25%;
					}
				}

				dt {
					width: 70px;
					flex-shrink: 0;
					font-size: 0.895rem;
					margin-top: 4px;
				}
				dd {
					margin: 0 0 0.5rem;
				}

				&:nth-child(2) {
					transform: translateX(40px);
					width: 80%;
				}
				&:nth-child(3) {
					transform: translateX(80px);
					width: 80%;
				}
				&:nth-child(4) {
					transform: translateX(120px);
					width: 70%;
				}
			}
		}
	}

	.head{
		flex-direction: column;

		h1{
			flex-basis: 100% !important;
			font-size: 1.75rem;
			margin-bottom: 1.875rem !important;
		}
	}
}

@media screen and (max-width: $mq-max-width-mobile) {
	.fields {
		$col-2-width-mobile: 100%;
		dl.info {
			dt {
				width: 100%;
			}
			dd {
				width: $col-2-width-mobile;
			}
		}

		aside {
			width: $col-2-width-mobile;
		}

		.results-nav-short {
			display: none;
		}

		.context-content {
			.level {
				&:not(:last-of-type) {
					dt:after {
						content: "";
						display: block;
						border-left: 2px solid $light-gray1;
						border-bottom: 2px solid $light-gray1;
						height: 90%;
						width: 1px;
						margin-left: 25%;
						margin-top: 25%;
					}
				}

				dt {
					width: 70px;
					flex-shrink: 0;
					font-size: 0.895rem;
					margin-top: 4px;
				}
				dd {
					margin: 0 0 0.5rem;
				}

				&:nth-child(2) {
					transform: translateX(20px);
					width: 80%;
				}
				&:nth-child(3) {
					transform: translateX(40px);
					width: 80%;
				}
				&:nth-child(4) {
					transform: translateX(60px);
					width: 70%;
				}
			}
		}
	}

	.results-nav a {
		padding: 1rem;
		&.prev,
		&.next {
			margin: 0;
		}
	}

	.related-items {
		flex-direction: column;
		li {
			flex-basis: 100%;
			height: 18vw;
			&:last-of-type {
				padding: 0.5rem;
			}
		}
	}
}
</style>
